import React, {
    useState, useMemo, useCallback, useRef, useEffect,
} from 'react';
import assertNever from 'helpers/assertNever';
import invariant from 'helpers/invariant';
import { NSCard, NSSelect } from 'bricks';
import { useAuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import useGetDealsWithProFormas from 'views/ProFormaTable/hooks/useGetDealsWithProFormas';
import useGenerateInvestmentMemoDeck from 'queries/InvestmentMemo/useGenerateInvestmentMemoDeck';
import useToast, { ToastType } from 'hooks/useToast';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import InvestmentMemoSideNav from './InvestmentMemoSideNav/InvestmentMemoSideNav';
import InvestmentMemoSummary from './InvestmentMemoSections/InvestmentMemoSummary/InvestmentMemoSummary';
import InvestmentMemoDealOverview from './InvestmentMemoSections/InvestmentMemoDealOverview/InvestmentMemoDealOverview';
import InvestmentMemoFinancials from './InvestmentMemoSections/InvestmentMemoFinancials/InvestmentMemoFinancials';
import './InvestmentMemo.scss';
import { initialInvestmentMemoFormData, InvestmentMemoNavItem } from './constants';
import {
    InvestmentMemoFormData,
    SelectedProFormaOption,
} from './types';
import { useInvestmentMemo } from './hooks/useInvestmentMemo';
import { useGenerateInvestmentMemoContent } from './hooks/useGenerateInvestmentMemoContent';
import { useValidation } from './hooks/useValidation';
import { hasEmptyRequiredFields } from './helpers';
import { InvestmentMemoContext } from './InvestmentMemoContext';

const getTaskStorageKey = (dealId: number, proFormaId: number) => `investmentMemo_task_${dealId}_${proFormaId}`;
const SELECTED_PROFORMA_STORAGE_KEY = 'investmentMemo_selectedProForma';

const InvestmentMemo = () => {
    const { selectedOrganizationId } = useAuthContext();
    const { showToast, showError } = useToast();
    const [activeNav, setActiveNav] = useState(InvestmentMemoNavItem.Summary);
    const [formData, setFormData] = useState<InvestmentMemoFormData>(initialInvestmentMemoFormData);
    const [selectedProFormaOption, setSelectedProFormaOption] = useState<SelectedProFormaOption | undefined>(undefined);

    const [pendingProFormaOption, setPendingProFormaOption] = useState<SelectedProFormaOption | undefined>(undefined);
    const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
    const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);

    const contentRef = useRef<HTMLDivElement>(null);

    const disableMemoGeneration = hasEmptyRequiredFields(formData);
    const {
        shouldShowError, getFieldError, hasSectionError, hasNavError,
    } = useValidation(formData, selectedProFormaOption);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [activeNav]);

    console.log(formData); // Keep for product for now
    invariant(selectedOrganizationId, 'Selected organization ID is required');
    const { data: dealsWithProFormas = [], isLoading: isLoadingDealsWithProFormas } = useGetDealsWithProFormas({
        orgId: selectedOrganizationId,
        statusId: PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE,
    });

    const { mutate: generateDeck, isPending: isGeneratingDeck } = useGenerateInvestmentMemoDeck();

    const {
        proFormaRoom,
        documentSuggestions,
        isLoadingProForma,
        isTitleAddressLoaded,
        isLoadingDocumentSuggestions,
    } = useInvestmentMemo(selectedProFormaOption, formData, setFormData);

    const addressFields = useMemo(() => ({
        address: formData.title.address,
        city: formData.title.city,
        state: formData.title.state,
        zipCode: formData.title.zipCode,
    }), [
        formData.title.address,
        formData.title.city,
        formData.title.state,
        formData.title.zipCode,
    ]);
    const { isGenerating, aiSuggestions } = useGenerateInvestmentMemoContent({
        selectedProFormaOption,
        organizationId: selectedOrganizationId,
        isTitleAddressLoaded,
        addressFields,
        setFormData,
    });

    useEffect(() => {
        if (selectedProFormaOption) {
            const dealId = selectedProFormaOption.value.dealId!;
            const proFormaId = selectedProFormaOption.value.id!;
            const storageKey = getTaskStorageKey(dealId, proFormaId);
            const savedTaskId = localStorage.getItem(storageKey);

            if (savedTaskId) {
                setCurrentTaskId(savedTaskId);
            } else {
                setCurrentTaskId(null);
            }
        }
    }, [selectedProFormaOption]);

    const handleGenerateDeck = (exportFormat: 'pdf' | 'pptx') => {
        const startTime = Date.now();
        invariant(selectedProFormaOption, 'Selected pro forma option is required');

        generateDeck(
            {
                organizationId: selectedOrganizationId,
                dealId: selectedProFormaOption.value.dealId!,
                proFormaId: selectedProFormaOption.value.id!,
                exportAs: exportFormat,
                formData,
            },
            {
                onSuccess: response => {
                    const endTime = Date.now();
                    const durationSeconds = ((endTime - startTime) / 1000).toFixed(1);
                    const timestamp = new Date().toLocaleTimeString();
                    console.log(
                        `[${timestamp}] Investment Memo deck generation started! - `
                        + `Format: ${exportFormat}, Duration: ${durationSeconds}s, Task ID: ${response.taskId}, Status: ${response.status}`,
                    );

                    setCurrentTaskId(response.taskId);
                    const storageKey = getTaskStorageKey(
                        selectedProFormaOption.value.dealId!,
                        selectedProFormaOption.value.id!,
                    );
                    localStorage.setItem(storageKey, response.taskId);

                    showToast(
                        (
                            <div>
                                <strong>Hang tight!</strong>
                                <br />
                                We&apos;re generating your memo and will save it to the deal&apos;s document library. This may take a few minutes.
                            </div>
                        ),
                        ToastType.Success,
                        { autoClose: 8000 },
                    );
                },
                onError: error => {
                    console.log(`Investment Memo deck generation failed - Format: ${exportFormat}, Error: ${error}`);
                    showError('There as an error generating this deck. Please try again.');
                },
            },
        );
    };

    const proFormaOptions = useMemo(() => {
        type ProFormaOption = { label: string; value: number; dealId: number; proFormaId: number };
        type GroupedOption = { label: string; options: ProFormaOption[] };
        const options: GroupedOption[] = [];

        dealsWithProFormas.forEach(deal => {
            const proFormaSelectOptions = deal.proFormas.map(proForma => ({
                label: proForma.name,
                value: proForma.id,
                dealId: deal.id,
                proFormaId: proForma.id,
            }));

            if (proFormaSelectOptions.length > 0) {
                options.push({
                    label: deal.dealName.toUpperCase(),
                    options: proFormaSelectOptions,
                });
            }
        });

        return options;
    }, [dealsWithProFormas]);

    useEffect(() => {
        if (proFormaOptions.length > 0 && !selectedProFormaOption) {
            try {
                const savedProForma = localStorage.getItem(SELECTED_PROFORMA_STORAGE_KEY);

                if (savedProForma) {
                    const { dealId, proFormaId } = JSON.parse(savedProForma);

                    const matchingProFormaOption = proFormaOptions
                        .flatMap(group => group.options)
                        .find(opt => opt.proFormaId === proFormaId && opt.dealId === dealId);

                    if (matchingProFormaOption) {
                        const selectedOption: SelectedProFormaOption = {
                            label: matchingProFormaOption.label,
                            value: {
                                type: 'proforma',
                                id: matchingProFormaOption.proFormaId,
                                dealId: matchingProFormaOption.dealId,
                            },
                        };
                        setSelectedProFormaOption(selectedOption);
                    } else {
                        localStorage.removeItem(SELECTED_PROFORMA_STORAGE_KEY);
                    }
                }
            } catch (error) {
                localStorage.removeItem(SELECTED_PROFORMA_STORAGE_KEY);
            }
        }
    }, [proFormaOptions, selectedProFormaOption]);

    const updateFormData = useCallback(<K extends keyof InvestmentMemoFormData>(
        section: K,
        data: Partial<InvestmentMemoFormData[K]>,
    ) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                ...data,
            },
        }));
    }, []);

    const navigateToSection = (navItem: InvestmentMemoNavItem) => {
        if (selectedProFormaOption) setActiveNav(navItem);
    };

    const saveSelectedProFormaToLocalStorage = (proFormaOption: SelectedProFormaOption) => {
        const proFormaData = {
            dealId: proFormaOption.value.dealId,
            proFormaId: proFormaOption.value.id,
        };
        localStorage.setItem(SELECTED_PROFORMA_STORAGE_KEY, JSON.stringify(proFormaData));
    };

    const handleProFormaSelection = (option: { label: string; value: number; dealId: number } | null) => {
        if (!option || !option.value) return;

        // Convert NSSelect option format back to the expected format
        const newSelectedProFormaOption: SelectedProFormaOption = {
            label: option.label,
            value: {
                type: 'proforma',
                id: option.value,
                dealId: option.dealId,
            },
        };

        if (selectedProFormaOption?.value.id === newSelectedProFormaOption.value.id) return;

        if (selectedProFormaOption) {
            setPendingProFormaOption(newSelectedProFormaOption);
            setIsChangeModalOpen(true);
        } else {
            setSelectedProFormaOption(newSelectedProFormaOption);
            saveSelectedProFormaToLocalStorage(newSelectedProFormaOption);
        }
    };

    const handleConfirmChange = () => {
        if (pendingProFormaOption) {
            setSelectedProFormaOption(pendingProFormaOption);
            saveSelectedProFormaToLocalStorage(pendingProFormaOption);
        }
        setIsChangeModalOpen(false);
        setPendingProFormaOption(undefined);
    };

    const handleCancelChange = () => {
        setIsChangeModalOpen(false);
        setPendingProFormaOption(undefined);
    };

    const handleDismissTaskStatus = () => {
        if (selectedProFormaOption) {
            const dealId = selectedProFormaOption.value.dealId!;
            const proFormaId = selectedProFormaOption.value.id!;
            const storageKey = getTaskStorageKey(dealId, proFormaId);
            localStorage.removeItem(storageKey);
        }
        setCurrentTaskId(null);
    };

    const handleTaskPollingError = (error?: Error) => {
        const errorMessage = error?.message || '';
        if (errorMessage.includes('timeout')) {
            showError('Task is taking longer than expected. Please check the document library later or contact support if the issue persists.');
        } else {
            showError('Unable to check task status. The task may still be running, but status updates are unavailable.');
        }
        handleDismissTaskStatus();
    };

    // Convert selectedProFormaOption to NSSelect format
    const selectedNSSelectValue = useMemo(() => {
        if (!selectedProFormaOption) return null;
        return {
            label: selectedProFormaOption.label,
            value: selectedProFormaOption.value.id,
            dealId: selectedProFormaOption.value.dealId,
            proFormaId: selectedProFormaOption.value.id,
        };
    }, [selectedProFormaOption]);

    const renderSection = () => {
        switch (activeNav) {
            case InvestmentMemoNavItem.Summary:
                return (
                    <InvestmentMemoSummary
                        disabled={!selectedProFormaOption}
                        formData={formData}
                        updateFormData={updateFormData}
                        navigateToSection={navigateToSection}
                        isGeneratingAIContent={isGenerating}
                        aiSuggestions={aiSuggestions}
                    />
                );
            case InvestmentMemoNavItem.DealOverview:
                invariant(selectedProFormaOption, 'Selected pro forma option is required');
                return (
                    <InvestmentMemoDealOverview
                        selectedProFormaOption={selectedProFormaOption}
                        formData={formData}
                        updateFormData={updateFormData}
                        navigateToSection={navigateToSection}
                        isGeneratingAIContent={isGenerating}
                        aiSuggestions={aiSuggestions}
                    />
                );
            case InvestmentMemoNavItem.Financials:
                invariant(selectedProFormaOption, 'Selected pro forma option is required');
                return (
                    <InvestmentMemoFinancials
                        formData={formData}
                        updateFormData={updateFormData}
                        proFormaRoom={proFormaRoom}
                    />
                );
            default:
                return assertNever(activeNav);
        }
    };

    return (
        <InvestmentMemoContext.Provider value={{
            documentSuggestions,
            isLoadingDocumentSuggestions,
            isLoadingProForma,
            selectedProForma: selectedProFormaOption,
            shouldShowError,
            getFieldError,
            hasSectionError,
            hasNavError,
        }}
        >
            <div className="InvestmentMemo d-flex overflow-hidden">
                <InvestmentMemoSideNav
                    activeNav={activeNav}
                    navigateToSection={navigateToSection}
                    selectedProFormaOption={selectedProFormaOption}
                    disableGenerationButton={disableMemoGeneration}
                    onGenerateDeck={handleGenerateDeck}
                    isGenerating={isGeneratingDeck}
                    currentTaskId={currentTaskId}
                    organizationId={selectedOrganizationId}
                    onDismissTaskStatus={handleDismissTaskStatus}
                    onTaskPollingError={handleTaskPollingError}
                />
                <div ref={contentRef} className="InvestmentMemo__content h-100 flex-grow-1 px-4">
                    <div className="d-flex justify-content-center pb-2 px-2">
                        <div className="w-75">
                            <NSCard className="mb-2 p-3">
                                <div className="text-primary font-13 mb-1 font-weight-bold">SELECT PRO FORMA</div>
                                <NSSelect
                                    name="proFormaSelect"
                                    options={proFormaOptions}
                                    value={selectedNSSelectValue}
                                    onChange={handleProFormaSelection}
                                    placeholder="Select pro forma"
                                    isDisabled={isLoadingDealsWithProFormas}
                                    isClearable={false}
                                />
                            </NSCard>
                            <hr />
                        </div>
                    </div>
                    {renderSection()}
                </div>
                <ConfirmationModal
                    isOpen={isChangeModalOpen}
                    toggle={handleCancelChange}
                    onConfirm={handleConfirmChange}
                    onCancel={handleCancelChange}
                    danger
                    modalHeaderText="Change Pro Forma"
                    modalFooterButtonText="Confirm"
                    modalFooterCancelButtonText="Cancel"
                >
                    Are you sure you want to select a new pro forma for this investment memo? Doing so will overwrite any work so far.
                </ConfirmationModal>
            </div>
        </InvestmentMemoContext.Provider>
    );
};

export default InvestmentMemo;
